import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { AIFeature } from '@/common/interface';

interface AIFeatureLockedProps {
    feature: AIFeature;
    /** Smaller layout for panels and chat boxes; hides the back button. */
    compact?: boolean;
}

/** Placeholder shown instead of an AI feature that is not included in the learner's plan. */
const AIFeatureLocked: React.FC<AIFeatureLockedProps> = ({ feature, compact = false }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const featureName = t(`common.aiPlanLimit.feature.${feature}`);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                gap: 1,
                width: '100%',
                height: compact ? 'auto' : '100%',
                minHeight: compact ? 0 : 360,
                p: compact ? 2 : 4,
                borderRadius: 2,
                bgcolor: compact ? 'background.paper' : 'transparent',
            }}
        >
            <Box
                sx={{
                    width: compact ? 40 : 64,
                    height: compact ? 40 : 64,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'info.light',
                    color: 'info.dark',
                }}
            >
                <LockOutlined fontSize={compact ? 'small' : 'large'} />
            </Box>
            <Typography variant={compact ? 'subtitle1' : 'h6'} fontWeight={700}>
                {t('common.aiPlanLimit.title.notInPlan')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
                {t('common.aiPlanLimit.description.notInPlan', { feature: featureName })}
            </Typography>
            {!compact && (
                <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/student/dashboard')}>
                    {t('common.aiPlanLimit.backToDashboard')}
                </Button>
            )}
        </Box>
    );
};

export default AIFeatureLocked;
